import dayjs from 'dayjs';
import { Button, Checkbox, FileInput, Label, Spinner, Textarea, TextInput, Select as FlowbiteSelect } from 'flowbite-react';
import React, { FormEvent, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { sendAssignmentToJobseeker, sendCaseStudyToJobseeker } from 'src/apis/application';
import { getPolicyForS3NonVideo } from 'src/apis/user';
import { useToast } from 'src/context/ToastProvider';
import { handleS3Upload } from 'src/utils/imageUpload';
import {
	AssignmentFileOption,
	AssignmentLinkOption,
	AssignmentSubmissionType,
	SendAssignmentToUserInput,
	SendCaseStudyToUserInput,
} from 'types';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { useUserContext } from 'src/context/UserProvider';
import { Dropdown, getJobSeekerAssignmentExpiry } from 'shared-utils';
import Select, { SingleValue } from 'react-select';
import { useGetAllPrepCoaches } from 'src/hooks/user.hooks';
import { get } from 'lodash';

export type SubmissionRoundVariant = 'assignment' | 'caseStudy';

type FormState = {
	positionId: string;
	userId: string;
	description?: string;
	fileLink?: string;
	link?: string;
	submissionType: AssignmentSubmissionType;
	linkOptions: AssignmentLinkOption[];
	fileOptions: AssignmentFileOption[];
	submissionNotes?: string;
	deadline?: Date;
	jobSeekerDeadline?: Date;
	prepCoach?: string;
};

type BaseProps = {
	userId?: string;
	userName?: string;
	positionId?: string;
	isLoading?: boolean;
	onFinish?: () => void;
};

type AssignmentProps = BaseProps & {
	variant: 'assignment';
	onSubmit?: (data: SendAssignmentToUserInput) => void;
};

type CaseStudyProps = BaseProps & {
	variant: 'caseStudy';
	onSubmit?: (data: SendCaseStudyToUserInput) => void;
};

type Props = AssignmentProps | CaseStudyProps;

const datePickerSx = {
	'&': { width: '100%' },
	'& .MuiInputBase-input': {
		padding: '10px',
		borderRadius: '8px',
		backgroundColor: 'white',
		border: '1px solid rgb(209 213 219 / 1)',
		fontSize: '14px',
	},
	'& .MuiInputBase-root': { outline: 'none', padding: '0px', '&:hover': { outline: 'none' } },
	'& fieldset': { display: 'none' },
};

const isValidUrl = (url: string): boolean => {
	if (!url?.trim()) return false;
	const trimmed = url.trim();
	try {
		const urlObj = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
		return ['http:', 'https:'].includes(urlObj.protocol) && urlObj.hostname.includes('.');
	} catch {
		return false;
	}
};

const RecruiterCandidateSendSubmissionRound = ({
	variant,
	userId,
	onSubmit,
	onFinish,
	userName,
	isLoading: outSideApiLoading = false,
	positionId,
}: Props) => {
	const isAssignment = variant === 'assignment';
	const roundLabel = isAssignment ? 'Assignment' : 'Case study';
	const toast = useToast();
	const queryClient = useQueryClient();
	const { isAccountManagerLoggedIn } = useUserContext();

	const [file, setFile] = React.useState<File | null>();
	const [isLoading, setIsLoading] = React.useState(outSideApiLoading);
	const [otherSubmissionUrl, setOtherSubmissionUrl] = React.useState('');
	const [formData, setFormData] = React.useState<FormState>({
		positionId: '',
		userId: '',
		prepCoach: '',
		submissionType: AssignmentSubmissionType.Link,
		linkOptions: [],
		fileOptions: [],
	});

	const submissionTypeOptions = useMemo(
		() => [
			{ label: 'Link', value: AssignmentSubmissionType.Link },
			{ label: 'File', value: AssignmentSubmissionType.File },
			{ label: 'Link & File', value: AssignmentSubmissionType.Both },
			{ label: 'Other', value: AssignmentSubmissionType.Other },
		],
		[],
	);

	const linkOptionList: { label: string; value: AssignmentLinkOption }[] = useMemo(
		() => [
			{ label: 'GitHub link', value: 'github' },
			{ label: 'Deployment link', value: 'deployment' },
			{ label: 'Drive link', value: 'drive' },
			{ label: 'Google Collab link', value: 'googleCollab' },
			{ label: 'Submission link', value: 'submission' },
			{ label: 'No Submission', value: 'noSubmission' },
		],
		[],
	);

	const fileOptionList: { label: string; value: AssignmentFileOption }[] = useMemo(
		() => [
			{ label: 'PDF', value: 'pdf' },
			{ label: 'DOC', value: 'doc' },
			{ label: 'DOCX', value: 'docx' },
			{ label: 'ZIP', value: 'zip' },
			{ label: 'Excel', value: 'excel' },
		],
		[],
	);

	const allowsLink =
		formData.submissionType === AssignmentSubmissionType.Link ||
		formData.submissionType === AssignmentSubmissionType.Both ||
		formData.submissionType === AssignmentSubmissionType.Other;
	const allowsFile =
		formData.submissionType === AssignmentSubmissionType.File ||
		formData.submissionType === AssignmentSubmissionType.Both ||
		formData.submissionType === AssignmentSubmissionType.Other;

	const requiresLinkOptions =
		formData.submissionType === AssignmentSubmissionType.Link ||
		formData.submissionType === AssignmentSubmissionType.Both;
	const requiresFileOptions =
		formData.submissionType === AssignmentSubmissionType.File ||
		formData.submissionType === AssignmentSubmissionType.Both;

	const hasContent = isAssignment
		? !!formData.description || !!formData.link || !!file
		: !!formData.description?.trim() && !!file;
	const submissionNotesRequired = formData.submissionType === AssignmentSubmissionType.Other;
	const isSubmissionConfigValid =
		(!requiresLinkOptions || formData.linkOptions.length > 0) &&
		(!requiresFileOptions || formData.fileOptions.length > 0) &&
		(!submissionNotesRequired || !!formData.submissionNotes);

	const resetForm = () => {
		setFormData({
			positionId: '',
			userId: '',
			prepCoach: '',
			submissionType: AssignmentSubmissionType.Link,
			linkOptions: [],
			fileOptions: [],
		});
		setFile(null);
		setOtherSubmissionUrl('');
	};

	const sendAssignmentFn = useMutation(['sendAssignmentToJobseeker'], sendAssignmentToJobseeker, {
		onSuccess: () => {
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			toast({ title: `Assignment sent to ${userName}`, type: 'success' });
			resetForm();
			onFinish?.();
		},
		onError: (error: Error) => {
			toast({ title: get(error, ['response', 'data', 'message'], error.message), type: 'error' });
		},
	});

	const sendCaseStudyFn = useMutation(['sendCaseStudyToJobseeker'], sendCaseStudyToJobseeker, {
		onSuccess: () => {
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			toast({ title: `Case study sent to ${userName}`, type: 'success' });
			resetForm();
			onFinish?.();
		},
		onError: (error: Error) => {
			toast({ title: get(error, ['response', 'data', 'message'], error.message), type: 'error' });
		},
	});

	const isSubmitting = isAssignment ? sendAssignmentFn.isLoading : sendCaseStudyFn.isLoading;
	const { data: prepCoachesData, isLoading: prepCoachesLoading } = useGetAllPrepCoaches();
	const prepCoachesOptions =
		prepCoachesData?.users?.map(u => ({ label: `${u.name} <${u.email}>`, value: String(u._id) })) ?? [];

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (isAssignment && !formData.description && !formData.link && !file) {
			toast({
				type: 'error',
				title: 'Add a description, link, or document for the assignment',
			});
			return;
		}

		if (!isAssignment) {
			if (!file) {
				toast({ type: 'error', title: 'Upload file is required for the case study' });
				return;
			}
			if (!formData.description?.trim()) {
				toast({ type: 'error', title: 'Description is required for the case study' });
				return;
			}
		}

		if (!isAssignment && !formData.deadline) {
			toast({ type: 'error', title: 'Submission deadline is required' });
			return;
		}

		if (isAssignment && formData.link?.trim() && !isValidUrl(formData.link)) {
			toast({ type: 'error', title: 'Please enter a valid assignment link (e.g., https://example.com/assignment)' });
			return;
		}

		if (formData.submissionType === AssignmentSubmissionType.Other && allowsLink && otherSubmissionUrl?.trim() && !isValidUrl(otherSubmissionUrl)) {
			toast({ type: 'error', title: 'Please enter a valid submission URL (e.g., https://forms.google.com/...)' });
			return;
		}

		if (allowsLink && !formData.linkOptions.length && formData.submissionType !== AssignmentSubmissionType.Other) {
			toast({ type: 'error', title: 'Select at least one allowed link submission type' });
			return;
		}

		if (allowsFile && !formData.fileOptions.length && formData.submissionType !== AssignmentSubmissionType.Other) {
			toast({ type: 'error', title: 'Select at least one allowed file submission type' });
			return;
		}

		if (formData.submissionType === AssignmentSubmissionType.Other) {
			if (!formData.submissionNotes) {
				toast({ type: 'error', title: 'Enter submission instructions for the "Other" submission type' });
				return;
			}
			if (allowsLink && !otherSubmissionUrl?.trim()) {
				toast({ type: 'error', title: 'Enter Submission URL for the "Other" submission type' });
				return;
			}
		}

		let uploadedFileUrl = formData.fileLink || '';
		if (file) {
			const fileSizeMB = file.size / (1024 * 1024);
			if (fileSizeMB > 5) {
				toast({ type: 'error', title: 'File size exceeds the maximum limit of 5 MB', duration: 10000 });
				return;
			}
			setIsLoading(true);
			const policy = await getPolicyForS3NonVideo(file.name, 'recruiterAssignments', file.type, true);
			const uploadedUrl = await handleS3Upload(policy, file);
			setIsLoading(false);
			if (uploadedUrl) uploadedFileUrl = uploadedUrl;
		}

		if (isAssignment) {
			const payload: SendAssignmentToUserInput = {
				positionId: positionId ?? '',
				userId: userId ?? '',
				prepCoach: formData.prepCoach ?? '',
				assignmentDescription: formData.description,
				assignmentFileLink: uploadedFileUrl,
				assignmentLink:
					formData.submissionType === AssignmentSubmissionType.Other && allowsLink ? otherSubmissionUrl : formData.link,
				assignmentSubmissionType: formData.submissionType,
				assignmentLinkOptions: formData.linkOptions,
				assignmentFileOptions: formData.fileOptions,
				assignmentSubmissionNotes: formData.submissionNotes,
				assignmentDeadline: formData.deadline,
				jobSeekerAssignmentDeadline: formData.jobSeekerDeadline,
			};
			if (onSubmit) {
				(onSubmit as AssignmentProps['onSubmit'])?.(payload);
			} else {
				sendAssignmentFn.mutate(payload);
			}
		} else {
			const payload: SendCaseStudyToUserInput = {
				positionId: positionId ?? '',
				userId: userId ?? '',
				caseStudyDescription: formData.description,
				caseStudyFileLink: uploadedFileUrl,
				caseStudySubmissionType: formData.submissionType,
				caseStudyLinkOptions: formData.linkOptions,
				caseStudyFileOptions: formData.fileOptions,
				caseStudySubmissionNotes: formData.submissionNotes,
				caseStudyDeadline: formData.deadline as Date,
			};
			if (onSubmit) {
				(onSubmit as CaseStudyProps['onSubmit'])?.(payload);
			} else {
				sendCaseStudyFn.mutate(payload);
			}
		}
	};

	useEffect(() => {
		resetForm();
	}, [variant]);

	useEffect(() => {
		setFormData(prev => {
			const submissionType = prev.submissionType ?? AssignmentSubmissionType.Link;
			const nextAllowsLink =
				submissionType === AssignmentSubmissionType.Link ||
				submissionType === AssignmentSubmissionType.Both ||
				submissionType === AssignmentSubmissionType.Other;
			const nextAllowsFile =
				submissionType === AssignmentSubmissionType.File ||
				submissionType === AssignmentSubmissionType.Both ||
				submissionType === AssignmentSubmissionType.Other;

			return {
				...prev,
				linkOptions: nextAllowsLink ? prev.linkOptions : [],
				fileOptions: nextAllowsFile ? prev.fileOptions : [],
				submissionNotes: submissionType === AssignmentSubmissionType.Other ? prev.submissionNotes : undefined,
			};
		});
	}, [formData.submissionType]);

	const submitDisabled =
		isSubmitting ||
		outSideApiLoading ||
		!hasContent ||
		!isSubmissionConfigValid ||
		(isAssignment && isAccountManagerLoggedIn && !formData.prepCoach) ||
		(!isAssignment && !formData.deadline);

	return (
		<form onSubmit={handleSubmit}>
			{isAssignment ? (
				<div className="mt-[16px]">
					<div className="mb-[8px] flex items-center gap-1">
						<Label>Assignment link</Label>
						<span className="text-[red]">*</span>
					</div>
					<TextInput
						type="url"
						placeholder="https://example.com/assignment"
						value={formData.link || ''}
						onChange={e => setFormData(prev => ({ ...prev, link: e.target.value }))}
						helperText="Enter a valid URL starting with http:// or https://"
					/>
				</div>
			) : null}

			<div className={isAssignment ? 'relative mt-[16px]' : 'relative'}>
				<div className="mb-[8px] flex items-center gap-1">
					<Label>Upload file</Label>
					{!isAssignment ? <span className="text-[red]">*</span> : null}
				</div>
				<FileInput
					id={`${variant}-submission-file-input`}
					accept={isAssignment ? '.pdf,.doc,.docx,.txt' : '.pdf,.doc,.docx'}
					disabled={isLoading}
					helperText={isAssignment ? 'Only txt, pdf, doc files allowed.' : 'Only pdf, doc, docx files allowed.'}
					className="w-full text-xs font-normal italic min-h-[42px] [&~p]:italic"
					onChange={event => {
						if (event.target.files?.[0]) setFile(event.target.files[0]);
					}}
				/>
			</div>

			<div className="mt-[16px]">
				<div className="mb-[8px] flex justify-between items-end">
					<div className="flex items-center gap-1">
						<Label>{isAssignment ? 'Assignment description' : 'Description'}</Label>
						{!isAssignment ? <span className="text-[red]">*</span> : null}
					</div>
					<span className="italic text-xs text-[#6B7280]">{(formData.description?.length || 0) + ' / 640'}</span>
				</div>
				<Textarea
					name="description"
					maxLength={640}
					value={formData.description || ''}
					placeholder={isAssignment ? 'Write your question here ...' : 'Describe the case study...'}
					className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[185px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
					onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
				/>
			</div>

			<div className="mt-[16px]">
				<div className="mb-[8px] flex items-center gap-1">
					<Label>{isAssignment ? 'Assignment submission type' : 'Submission type'}</Label>
					<span className="text-[red]">*</span>
				</div>
				<FlowbiteSelect
					value={formData.submissionType}
					onChange={e =>
						setFormData(prev => ({ ...prev, submissionType: e.target.value as AssignmentSubmissionType }))
					}
				>
					{submissionTypeOptions.map(option => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</FlowbiteSelect>
			</div>

			{allowsLink && formData.submissionType !== AssignmentSubmissionType.Other ? (
				<div className="mt-[16px]">
					<Label className="mb-[8px] block">Allowed link formats</Label>
					<div className="grid grid-cols-2 gap-2">
						{linkOptionList.map(option => (
							<label key={option.value} className="flex items-center gap-2 text-sm">
								<Checkbox
									checked={formData.linkOptions.includes(option.value)}
									onChange={event => {
										const checked = event.target.checked;
										setFormData(prev => ({
											...prev,
											linkOptions: checked
												? [...prev.linkOptions, option.value]
												: prev.linkOptions.filter(val => val !== option.value),
										}));
									}}
								/>
								<span>{option.label}</span>
							</label>
						))}
					</div>
				</div>
			) : null}

			{allowsFile && formData.submissionType !== AssignmentSubmissionType.Other ? (
				<div className="mt-[16px]">
					<Label className="mb-[8px] block">Allowed file formats</Label>
					<div className="grid grid-cols-2 gap-2">
						{fileOptionList.map(option => (
							<label key={option.value} className="flex items-center gap-2 text-sm">
								<Checkbox
									checked={formData.fileOptions.includes(option.value)}
									onChange={event => {
										const checked = event.target.checked;
										setFormData(prev => ({
											...prev,
											fileOptions: checked
												? [...prev.fileOptions, option.value]
												: prev.fileOptions.filter(val => val !== option.value),
										}));
									}}
								/>
								<span>{option.label}</span>
							</label>
						))}
					</div>
				</div>
			) : null}

			{formData.submissionType === AssignmentSubmissionType.Other ? (
				<>
					{allowsLink ? (
						<div className="mt-[16px]">
							<div className="mb-[8px] flex items-center gap-1">
								<Label>Submission URL</Label>
								<span className="text-[red]">*</span>
							</div>
							<TextInput
								type="url"
								placeholder="https://forms.google.com/..."
								value={otherSubmissionUrl}
								helperText="Enter a valid URL starting with http:// or https://"
								onChange={e => setOtherSubmissionUrl(e.target.value)}
							/>
							<p className="mt-1 text-xs text-gray-500">
								Enter the URL where students should submit their {roundLabel.toLowerCase()} (e.g., Google Form, Typeform,
								etc.)
							</p>
						</div>
					) : null}
					<div className="mt-[16px]">
						<div className="mb-[8px] flex items-center gap-1">
							<Label>Submission instructions</Label>
							<span className="text-[red]">*</span>
						</div>
						<Textarea
							value={formData.submissionNotes || ''}
							placeholder={`Describe how the jobseeker should submit the ${roundLabel.toLowerCase()}`}
							onChange={e => setFormData(prev => ({ ...prev, submissionNotes: e.target.value }))}
						/>
					</div>
				</>
			) : null}

			{isAssignment && isAccountManagerLoggedIn ? (
				<div className="mt-[16px] whiteBgSelect">
					<div className="mb-[8px]">
						<Label>Prep Coach</Label>
						<span className="text-[red]">*</span>
					</div>
					<Select
						classNamePrefix="my-select-plans"
						className="single-value-select-tag"
						isLoading={prepCoachesLoading}
						options={prepCoachesOptions}
						closeMenuOnSelect
						value={prepCoachesOptions.find(op => op.value === formData.prepCoach)}
						onChange={(val: SingleValue<Dropdown>) => {
							if (val) setFormData(prev => ({ ...prev, prepCoach: String(val.value) }));
						}}
					/>
				</div>
			) : null}

			<div className="mt-[16px]">
				<div className="mb-[8px]">
					<Label>Submission deadline</Label>
					{isAssignment ? (
						<span className="text-[gray]"> (Optional)</span>
					) : (
						<span className="text-[red]"> *</span>
					)}
				</div>
				<MobileDateTimePicker
					value={formData.deadline ? dayjs(formData.deadline) : null}
					minDateTime={dayjs()}
					format="DD MMM, YYYY, hh:mm A"
					onChange={date => {
						const dateValue = date as { $d?: Date };
						const nextDeadline = dateValue?.$d || undefined;
						setFormData(prev => ({
							...prev,
							deadline: nextDeadline,
							jobSeekerDeadline:
								isAssignment && nextDeadline ? dayjs(getJobSeekerAssignmentExpiry(nextDeadline)).toDate() : prev.jobSeekerDeadline,
						}));
					}}
					sx={datePickerSx}
				/>
			</div>

			{isAssignment && isAccountManagerLoggedIn ? (
				<div className="mt-[16px]">
					<div className="mb-[8px]">
						<Label>Jobseeker Submission deadline</Label>
						<span className="text-[gray]"> (Optional)</span>
					</div>
					<MobileDateTimePicker
						value={formData.jobSeekerDeadline ? dayjs(formData.jobSeekerDeadline) : null}
						minDateTime={dayjs()}
						format="DD MMM, YYYY, hh:mm A"
						onChange={date => {
							const dateValue = date as { $d?: Date };
							setFormData(prev => ({ ...prev, jobSeekerDeadline: dateValue?.$d || undefined }));
						}}
						sx={datePickerSx}
					/>
				</div>
			) : null}

			<Button type="submit" className="w-full min-h-[42px] mt-[20px] bg-[#7777E7]" color="purplePlaced" disabled={submitDisabled}>
				{isSubmitting || outSideApiLoading ? (
					<div className="absolute flex justify-center inset-0 rounded-[inherit] items-center w-full h-full backdrop-blur-md">
						<Spinner />
					</div>
				) : (
					`Send ${isAssignment ? 'Assignment' : 'Case Study'}`
				)}
			</Button>
		</form>
	);
};

export default RecruiterCandidateSendSubmissionRound;
